import type { BlogCategory, BlogPost } from "@/types/content";
import { spinePosts } from "@/data/blog/spine-posts";
import { jointPosts } from "@/data/blog/joint-posts";
import { bonePosts } from "@/data/blog/bone-posts";
import { sportsPosts } from "@/data/blog/sports-posts";
import { generalPosts } from "@/data/blog/general-posts";
import { blogImageFallback, blogImages } from "@/data/blog/images";

// ---------------------------------------------------------------------------
// BLOG CONTENT — easy to manage
// ---------------------------------------------------------------------------
// All 50 articles are stored in the topic files under src/data/blog/.
// To add or edit an article, change the matching array there — no component
// changes are needed. Articles are sorted newest-first automatically, and any
// accidental duplicate slug is dropped so every URL always resolves to exactly
// one article.
//
// IMAGES: every article gets its own topic-specific visual from
// src/data/blog/images.ts (keyed by slug). That map is the single place to
// change an article image, and it guarantees no two unrelated articles share
// the same picture.
// ---------------------------------------------------------------------------

const allPosts: BlogPost[] = [
  ...spinePosts,
  ...jointPosts,
  ...bonePosts,
  ...sportsPosts,
  ...generalPosts,
];

/** Attach the article-specific image + accessible alt text. */
const withImages = (post: BlogPost): BlogPost => {
  const match = blogImages[post.slug];
  return {
    ...post,
    featuredImage: match?.src || post.featuredImage || blogImageFallback,
    imageAlt: match?.alt || post.imageAlt || post.title,
  };
};

/** Remove duplicate slugs (first occurrence wins) so routes stay unique. */
const uniquePosts = allPosts
  .filter((post, index) => allPosts.findIndex((item) => item.slug === post.slug) === index)
  .map(withImages);

/** Newest first — keeps the featured article and the listing in sync. */
export const blogPosts: BlogPost[] = [...uniquePosts].sort(
  (a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
);

/** Categories in the order they should appear, including the "All" filter. */
export const blogCategoryOrder: BlogCategory[] = [
  "Spine Health",
  "Back Pain",
  "Neck Pain",
  "Joint Health",
  "Knee Health",
  "Shoulder Health",
  "Bone Health",
  "Sports Injuries",
  "Patient Education",
];

/** Only categories that actually have posts, ready for the filter tabs. */
export const blogCategories = blogCategoryOrder.filter((category) =>
  blogPosts.some((post) => post.category === category)
);

/** The most recent article, used as the featured story on the blog index. */
export const featuredPost: BlogPost | undefined = blogPosts[0];

export function getPostBySlug(slug: string): BlogPost | undefined {
  if (!slug) {
    return undefined;
  }
  let decoded = slug;
  try {
    decoded = decodeURIComponent(slug);
  } catch {
    decoded = slug;
  }
  return blogPosts.find((post) => post.slug === decoded);
}

export function getRelatedPosts(current: BlogPost, limit = 3): BlogPost[] {
  const sameCategory = blogPosts.filter(
    (post) => post.category === current.category && post.slug !== current.slug
  );
  if (sameCategory.length >= limit) {
    return sameCategory.slice(0, limit);
  }
  const others = blogPosts.filter(
    (post) => post.slug !== current.slug && post.category !== current.category
  );
  return [...sameCategory, ...others].slice(0, limit);
}